import { Button } from '@betty/moonlight';
import { memo, useCallback, useMemo } from 'react';
import {
  useDispatch,
  useSelector,
  selectAuthenticationStatus,
} from '@betty/toolkit/state';
import {
  getPromos,
  optIn,
  selectAnotherOptedInPromoByType,
  selectOptInPending,
} from '../../state/promos/promos.slice';
import usePromoClick from '../../hooks/usePromoClick';
import { BottomSheet, useNavigation } from '@betty/navigation';
import { Promotion } from '../../state/promos/promos.types';
import { ButtonProps } from '@betty/moonlight/lib/common/Button/Button.types';

function PromoCta({ promo }: { promo: Promotion }) {
  const dispatch = useDispatch();
  const navigation = useNavigation();

  const handleCtaAction = usePromoClick(promo);
  const authenticationStatus = useSelector(selectAuthenticationStatus);
  const isLoading = useSelector(selectOptInPending(promo.promotionId));
  const anotherOptedInPromo = useSelector(
    selectAnotherOptedInPromoByType(promo.promotionId, promo.promoType),
  );

  const handleOptIn = useCallback(async () => {
    const result = await dispatch(optIn(promo.promotionId));

    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(getPromos());
      handleCtaAction();
    }
  }, [promo, dispatch, handleCtaAction]);

  const onClick = useCallback(() => {
    if (promo.status === 'Locked') {
      return;
    } else if (authenticationStatus === 'unauthenticated') {
      navigation.navigate('RegistrationScreen');
    } else if (
      promo.isOptedIn ||
      promo.promoType === 'Static' ||
      promo.contentType === 'Referrer'
    ) {
      handleCtaAction();
    } else if (anotherOptedInPromo) {
      BottomSheet.open({
        namespace: 'promos',
        type: 'OptedInPromo',
        data: {
          handleCtaAction: () => handleCtaAction(),
          promotionId: promo.promotionId,
          title: anotherOptedInPromo.title,
          promoType: anotherOptedInPromo.promoType,
        },
      });
    } else {
      handleOptIn();
    }
  }, [
    promo.status,
    promo.isOptedIn,
    promo.promoType,
    promo.contentType,
    promo.promotionId,
    authenticationStatus,
    anotherOptedInPromo,
    navigation,
    handleCtaAction,
    handleOptIn,
  ]);

  const ctaLocKey = useMemo(() => {
    if (promo.status === 'Locked') {
      return promo.lockedDescription as ButtonProps['locKey']; // TODO: Localize with API changes
    } else if (promo.contentType === 'Referrer') {
      return 'CtaLetsGo';
    } else if (
      promo.promoType === 'Welcome' ||
      promo.promoType === 'Static' ||
      promo.isOptedIn
    ) {
      return promo.cta
        ? (`Cta${promo.cta.type}` as ButtonProps['locKey'])
        : undefined;
    } else {
      return 'wantThisPromo';
    }
  }, [promo]);

  if (!ctaLocKey) {
    return null;
  }

  return (
    <Button
      variant="primary"
      alignSelf="center"
      locNS="promos"
      locKey={ctaLocKey}
      leftIcon={promo.status === 'Locked' ? 'lockFilled' : undefined}
      onPress={onClick}
      loading={isLoading}
      width="100%"
    />
  );
}

export default memo(PromoCta);
